<template>
  <VApp>
    <VMain @contextmenu.native.prevent="openMenu">
      <VMenu
        v-model="showMenu"
        :position-x="menuX"
        :position-y="menuY"
        absolute
        offset-y
      >
        <VList dense>
          <VListItem @click="showRawData = !showRawData">
            <VListItemAction class="mr-2">
              <VIcon small>
                {{ showRawData ? '$check-square' : '$checkboxOff' }}
              </VIcon>
            </VListItemAction>
            <VListItemContent>
              <VListItemTitle>Show Raw Data</VListItemTitle>
            </VListItemContent>
          </VListItem>
          <VListItem
            href="https://form.asana.com?k=QHbwD-d7-QDJRPHeUZrDoQ&d=338667169953"
            target="_blank"
            rel="noopener"
          >
            <VListItemAction class="mr-2">
              <VIcon small>$comments</VIcon>
            </VListItemAction>
            <VListItemContent>
              <VListItemTitle>Send feedback</VListItemTitle>
            </VListItemContent>
          </VListItem>
          <VDivider />
          <VListItem disabled>
            <VListItemContent>
              <VListItemTitle class="caption text--secondary">
                {{ branch }} &bull; {{ commitRef }} &bull; {{ buildAge }}
              </VListItemTitle>
            </VListItemContent>
          </VListItem>
          <VListItem @click="showSalesforceUser">
            <VListItemAction class="mr-2">
              <VIcon small>$user</VIcon>
            </VListItemAction>
            <VListItemContent>
              <VListItemTitle>
                {{ userTypeLabel }}
              </VListItemTitle>
            </VListItemContent>
          </VListItem>
          <VListItem v-if="localDev && !claudePreview" @click="copyTokens">
            <VListItemAction class="mr-2">
              <VIcon small>$copy</VIcon>
            </VListItemAction>
            <VListItemContent>
              <VListItemTitle>{{ copyLabel }}</VListItemTitle>
            </VListItemContent>
          </VListItem>
          <VListItem @click="clearTokens">
            <VListItemAction class="mr-2">
              <VIcon small>$times-circle</VIcon>
            </VListItemAction>
            <VListItemContent>
              <VListItemTitle>Sign out of Salesforce</VListItemTitle>
            </VListItemContent>
          </VListItem>
        </VList>
      </VMenu>
      <VSnackbar v-model="showUserSnackbar" :timeout="5000" bottom>
        {{ salesforceUserLabel }}
      </VSnackbar>
      <VSnackbar v-model="toastVisible" :timeout="2000" bottom>
        {{ toastMessage }}
      </VSnackbar>
      <div ref="content">
        <VAlert
          v-model="showVueError"
          type="error"
          dismissible
          transition="fade-transition"
        >
          Oops, something went wrong
          <pre
            class="caption"
            style="white-space: pre-wrap; word-break: break-word"
            v-text="vueError"
          />
        </VAlert>
        <VAlert
          v-if="fetchErrors && fetchErrors.length"
          type="warning"
          dense
          dismissible
          class="caption mb-2"
        >
          Some data may be incomplete.
          <div
            v-for="(err, i) in fetchErrors"
            :key="i"
            class="caption"
            v-html="err"
          />
        </VAlert>
        <div v-if="isPending" class="px-1 pt-1 pb-4">
          <VSkeletonLoader
            type="list-item-avatar, list-item-three-line, divider, list-item-avatar, list-item-three-line"
            elevation="1"
          />
        </div>
        <div
          v-else-if="!accessToken && !showDemoPreview"
          class="text-center pa-2"
        >
          <SalesforceConnectButton />
        </div>
        <template v-else>
          <div v-if="!accessToken" class="text-center pa-2">
            <SalesforceConnectButton />
          </div>
          <SearchBox
            ref="searchBox"
            :initial-email="requesterEmail"
            :readonly="!accessToken && !isDemoActive"
            @live-filter="onLiveFilter"
            @search="onSearch"
          />
          <div
            v-if="liveFilterResult && accountMatches.length <= 1"
            class="caption text--disabled mx-2 mb-1"
            style="margin-top: -4px; font-size: 11px; letter-spacing: 0.01em"
          >
            Filtering this customer<template v-if="liveFilterQuery.length >= 3">
              ·
              <VTooltip bottom open-delay="150" max-width="360">
                <template #activator="{ on, attrs }">
                  <span
                    class="filter-search-link"
                    v-bind="attrs"
                    v-on="on"
                    @click="forceSearchFromHint"
                    >search all customers</span
                  >
                </template>
                <span style="font-size: 11px">
                  Search all customers by email, phone, order #, or
                  case-sensitive proof ID prefix (Shift+Enter)
                </span>
              </VTooltip>
            </template>
          </div>
          <div
            v-else-if="searchHint && accountMatches.length <= 1"
            class="caption text--secondary mx-2 mb-1"
            style="margin-top: -4px"
            v-text="searchHint"
          />
          <VSelect
            v-if="accountMatches.length > 1"
            :items="accountMatchItems"
            :value="requesterEmail"
            item-text="label"
            item-value="email"
            dense
            outlined
            hide-details
            :menu-props="{ offsetY: true, bottom: true }"
            class="caption mx-1 mb-1 match-dropdown"
            style="font-size: 12px"
            @change="selectMatch"
          >
            <template #prepend-inner>
              <VIcon class="mr-1 match-dropdown-icon"> $user </VIcon>
            </template>
            <template #item="{ item }">
              <VListItemContent class="py-1">
                <VListItemTitle class="caption text-truncate">
                  {{ item.name || item.email }}
                </VListItemTitle>
                <VListItemSubtitle class="caption text-truncate">
                  {{ item.email }}
                  <span v-if="item.matchContext" class="text--disabled ml-1">
                    · {{ item.matchContext }}
                  </span>
                </VListItemSubtitle>
              </VListItemContent>
            </template>
            <template #selection>
              <span class="caption text-truncate">
                {{ accountMatches.length }} matches
                <span v-if="searchQuery" class="text--secondary">
                  · {{ searchHint || searchQuery }}
                </span>
              </span>
            </template>
          </VSelect>
          <div v-if="isSearching" class="px-1 pt-1 pb-4">
            <VSkeletonLoader
              type="list-item-avatar, list-item-three-line, divider, list-item-avatar, list-item-three-line"
              elevation="1"
            />
          </div>
          <template v-else-if="hasCustomerData">
            <Customer
              :account="account"
              :salesforce-orders="salesforceOrders"
              :proofs="proofs"
              :ticket="ticket"
              :tickets="tickets"
              :original-requester-email="originalRequesterEmail"
            />
            <Orders
              :orders="orders"
              :salesforce-orders="salesforceOrders"
              :proofs="proofs"
              :highlight-order-number="highlightOrderNumber"
              :filter-order-numbers="
                liveFilterResult ? liveFilterResult.orderNumbers : null
              "
              :filter-proof-ids="
                liveFilterResult
                  ? liveFilterResult.matches
                      .filter((m) => m.type === 'proof')
                      .map((m) => m.id)
                  : null
              "
            />
            <VDivider class="my-4" />
            <Proofs
              :proofs="unpaidProofs"
              :highlight-proof-id="highlightProofId"
              :filter-proof-ids="
                liveFilterResult ? liveFilterResult.proofIds : null
              "
            />
            <template v-if="showRawData">
              <VDivider class="my-4" />
              <RawData class="pa-1" />
            </template>
          </template>
        </template>
      </div>
    </VMain>
  </VApp>
</template>

<script lang="ts">
import Vue, { computed, defineComponent, onMounted, provide, ref } from 'vue'
import { usePromise } from 'vue-promised'
import { getModule } from 'vuex-module-decorators'

import Customer from '@/components/Customer.vue'
import Orders from '@/components/Orders.vue'
import Proofs from '@/components/Proofs.vue'
import RawData from '@/components/RawData.vue'
import store from '@/store'
import PaperCulture from '@/store/modules/paperCulture'
import Salesforce, {
  AccountMatch,
  normalizeUSPhone,
  SALESFORCE_INSTANCE_URL
} from '@/store/modules/salesforce'
import Zendesk, {
  getStoredDevEmail,
  isOutsideZendesk,
  LAST_DEV_EMAIL_KEY
} from '@/store/modules/zendesk'
import { initializeAnalytics } from '@/utils/analytics'
import { isClaudePreview, isLocalDev } from '@/utils/claudePreview'
import { DEMO_EMAIL, isDemoEmail } from '@/utils/demoMode'
import { outerHeight } from '@/utils/dom'
import { filterLocalData, LocalFilterResult } from '@/utils/localSearchFilter'
import {
  couldBeOrderNumber,
  detectFormat,
  formatOrder,
  formatPhone,
  normalizeOrderNumber,
  normalizeProofId
} from '@/utils/searchDetection'

import SalesforceConnectButton from './components/SalesforceConnectButton.vue'
import SearchBox from './components/SearchBox.vue'

export default defineComponent({
  components: {
    Customer,
    SearchBox,
    Orders,
    Proofs,
    SalesforceConnectButton,
    RawData
  },
  setup() {
    const env = process.env
    const localDev = isLocalDev()
    // Show demo UI when unauthenticated only if ?demo is in the URL
    const showDemoPreview = new URLSearchParams(window.location.search).has(
      'demo'
    )
    const claudePreview = isClaudePreview()

    // Show errors in the UI so the user knows if something weird is going on
    const vueError = ref<string>()
    const showVueError = ref(false)
    // TODO: Consider if we need to catch other types of errors, or if we should use Sentry's user feedback dialog
    const oldErrorHandler = Vue.config.errorHandler
    Vue.config.errorHandler = (errorObject, vm, info) => {
      if (errorObject?.stack) {
        // Show error and first 2 lines of stack trace
        vueError.value = errorObject.stack.split('\n').slice(0, 3).join('\n')
        // If another error occurs after the alert is dismissed, show the alert again
        showVueError.value = true
      }
      if (oldErrorHandler) {
        // Call existing error handler
        oldErrorHandler(errorObject, vm, info)
      } else {
        // Rethrow if there are no other handlers that may log to console
        throw errorObject
      }
    }

    const zendesk = getModule(Zendesk)
    const salesforce = getModule(Salesforce)
    const paperCulture = getModule(PaperCulture)

    // Context menu
    const showMenu = ref(false)
    const menuX = ref(0)
    const menuY = ref(0)
    const showRawData = ref(false)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const searchBox = ref<any>(null)
    const accountMatches = ref<AccountMatch[]>([])
    const searchHint = ref<string | null>(null)
    const searchQuery = ref('')
    const highlightProofId = ref<string | null>(null)
    const highlightOrderNumber = ref<string | null>(null)
    const liveFilterResult = ref<LocalFilterResult | null>(null)
    const liveFilterQuery = ref('')
    const isSearching = ref(false)
    const copyLabel = ref('Copy Token')

    function openMenu(e: MouseEvent) {
      menuX.value = e.clientX
      menuY.value = e.clientY
      showMenu.value = true
    }

    const buildTime = env.VUE_APP_BUILD_TIME
    function formatBuildAge(): string {
      if (!buildTime) return 'local dev'
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const T = 'Temporal' in window ? (window as any).Temporal : undefined
      if (T) {
        const built = T.Instant.from(buildTime)
        const now = T.Now.instant()
        const { hours, minutes } = now.since(built, {
          largestUnit: 'hour'
        })
        if (hours >= 24) return `${Math.round(hours / 24)}d ago`
        if (hours >= 1) return `${hours}h ago`
        if (minutes < 1) return 'just now'
        return `${minutes}m ago`
      }
      const ms = Date.now() - new Date(buildTime).getTime()
      const mins = Math.round(ms / 60_000)
      if (mins < 1) return 'just now'
      if (mins < 60) return `${mins}m ago`
      const hrs = Math.round(mins / 60)
      if (hrs < 24) return `${hrs}h ago`
      return `${Math.round(hrs / 24)}d ago`
    }

    function clearTokens() {
      salesforce.clearTokens()
    }

    const salesforceUserLabel = ref('')
    const showUserSnackbar = ref(false)

    const userType = ref<string | null>(null)

    async function fetchUserInfo() {
      if (!salesforce.accessToken) return
      try {
        const resp = await fetch(
          SALESFORCE_INSTANCE_URL + '/services/oauth2/userinfo',
          { headers: { Authorization: `Bearer ${salesforce.accessToken}` } }
        )
        const data = await resp.json()
        userType.value = data.user_type || null
        salesforceUserLabel.value = [
          data.name,
          data.email || data.preferred_username,
          data.user_type
        ]
          .filter(Boolean)
          .join(' · ')
      } catch {
        salesforceUserLabel.value = 'Error loading user'
      }
    }

    async function showSalesforceUser() {
      if (!salesforceUserLabel.value) {
        salesforceUserLabel.value = 'Loading…'
      }
      showUserSnackbar.value = true
      await fetchUserInfo()
    }

    async function copyTokens() {
      const payload = JSON.stringify(
        {
          accessToken: localStorage.getItem('salesforceAccessToken'),
          refreshToken: localStorage.getItem('salesforceRefreshToken')
        },
        null,
        2
      )
      await navigator.clipboard.writeText(payload)
      copyLabel.value = 'Copied!'
      setTimeout(() => {
        copyLabel.value = 'Copy Token'
      }, 2000)
    }

    const content = ref<HTMLElement>()

    /**
     * Resize the iframe to the outer height of the content container.
     *
     * Always measures the element rather than trusting an observer-reported size, so margins
     * and any late layout shifts (images/fonts finishing, transitions settling) are included.
     */
    async function resize() {
      if (content.value) {
        await zendesk.resize(outerHeight(content.value))
      }
    }

    async function loadDemoData() {
      // Lazy-load mock data to avoid bundling it when not used
      const [demo, { seedTrackingCache }] = await Promise.all([
        import('@/mock/demoData'),
        import('@/services/trackingCache')
      ])
      salesforce.setDemoData({
        account: demo.demoAccount,
        proofs: demo.demoSalesforceProofs,
        orders: demo.demoSalesforceOrders
      })
      paperCulture.setOrders(demo.demoPaperCultureOrders)
      zendesk.setDemoTickets(demo.demoTickets)
      seedTrackingCache(demo.demoTrackingResults)
      // Set display name from mock account
      if (demo.demoAccount.firstName || demo.demoAccount.lastName) {
        zendesk.setRequester({
          email: zendesk.ticket.requester.email,
          name: [demo.demoAccount.firstName, demo.demoAccount.lastName]
            .filter(Boolean)
            .join(' ')
        })
      }
    }

    /**
     * Fetches Salesforce and Paper Culture data, syncing the requester name
     * from the Salesforce account. Requires a Salesforce access token.
     *
     * Demo mode (example@paperculture.com) loads mock data instead of calling APIs.
     * When unauthenticated, demo data is always loaded regardless of email.
     *
     * Unauthenticated non-email searches (e.g. proof ID prefix) can't hit the
     * Salesforce API, so they only use the live local filter. On submit, we
     * restore the demo email and show a hint to sign in.
     */
    async function fetchAuthenticated() {
      if (isDemoEmail(zendesk.ticket.requester.email)) {
        await loadDemoData()
        return
      }
      // Unauthenticated: non-email searches can't hit the API
      if (!salesforce.accessToken) {
        const email = zendesk.ticket.requester.email
        if (email && !email.includes('@')) {
          searchHint.value =
            'Sign in to Salesforce to search by order #, phone, or proof ID.'
          // Restore the demo email so the demo data stays loaded
          zendesk.setRequester({ email: DEMO_EMAIL })
          searchBox.value?.setEmail(email)
          return
        }
      }
      if (salesforce.accessToken) {
        let email = zendesk.ticket.requester.email
        // Clear previous customer data during search so stale data
        // doesn't display with the raw search term
        salesforce.setDemoData({ account: null, proofs: [], orders: [] })
        paperCulture.clearOrders()
        accountMatches.value = []
        searchHint.value = null
        searchQuery.value = ''
        highlightProofId.value = null
        highlightOrderNumber.value = null
        if (email && !email.includes('@')) {
          searchQuery.value = email
          const isPhone = !!normalizeUSPhone(email)
          const digits = email.replace(/\D/g, '')
          const isAllDigits =
            digits.length === email.trim().replace(/[\s+()-]/g, '').length
          const couldBeOrder = couldBeOrderNumber(email)
          let matches: AccountMatch[] = []
          const hints: string[] = []

          // When ambiguous (could be both order and phone), search both
          if (couldBeOrder) {
            const orderNorm = normalizeOrderNumber(email)
            const orderValue = orderNorm?.value || digits
            const orderMatches =
              await salesforce.lookupByOrderNumber(orderValue)
            if (orderMatches.length) {
              matches.push(...orderMatches)
              hints.push(`order ${formatOrder(orderValue)}`)
            }
          }
          if (isPhone) {
            let phoneMatches = await salesforce.lookupByPhone(email)
            if (!phoneMatches.length) {
              phoneMatches = await salesforce.lookupByPhoneViaOrders(email)
            }
            if (phoneMatches.length) {
              // Merge, deduplicate by email
              const seen = new Set(matches.map((m) => m.email))
              for (const m of phoneMatches) {
                if (!seen.has(m.email)) {
                  seen.add(m.email)
                  matches.push(m)
                }
              }
              const normalized = normalizeUSPhone(email)
              hints.push(
                `phone ${normalized ? formatPhone(normalized) : email}`
              )
            }
          }

          if (hints.length) {
            searchHint.value = `Found via ${hints.join(' and ')}`
          }

          // Try proof ID (normalize CB32/TypeID to Base64url for lookup)
          if (!matches.length && !isAllDigits && email.length >= 3) {
            const proofQuery = normalizeProofId(email) || email
            matches = await salesforce.lookupByProofId(proofQuery)
            searchHint.value = matches.length
              ? `Proof ID starting with "${proofQuery}"`
              : `No proof ID found starting with "${email}" (case-sensitive)`
          }

          // No matches from any method
          if (!matches.length && !searchHint.value) {
            const orderNorm = normalizeOrderNumber(email)
            const orderFmt = formatOrder(orderNorm?.value || digits)
            const normalized = normalizeUSPhone(email)
            const phoneFmt = normalized ? formatPhone(normalized) : email
            if (isPhone && couldBeOrder) {
              searchHint.value = `No match for order ${orderFmt} or phone ${phoneFmt}`
            } else if (isPhone) {
              searchHint.value = `No customer found for ${phoneFmt}`
            } else if (couldBeOrder) {
              searchHint.value = `No order found for ${orderFmt}`
            } else {
              searchHint.value = `No match found for "${email}"`
            }
          }
          if (matches.length >= 1) {
            const selected = matches[0]
            email = selected.email
            zendesk.setRequester({ email, name: selected.name })
            searchBox.value?.setEmail(email)
            highlightProofId.value = selected.matchedProofId || null
            highlightOrderNumber.value = selected.matchedOrderNumber || null
            if (matches.length > 1) {
              accountMatches.value = matches
            }
          } else {
            // No matches from any lookup — don't query Salesforce
            // with a non-email string that would fail or return nothing
            return
          }
        } else {
          searchHint.value = null
        }
        try {
          await salesforce.fetchAll(email)
        } catch (e: unknown) {
          // If tokens were cleared (revoked), the user will see the sign-in
          // screen on re-render — don't crash the app.
          if (!salesforce.accessToken) {
            console.warn('[App] Salesforce session expired, showing sign-in')
            return
          }
          throw e
        }
        if (salesforce.account?.firstName || salesforce.account?.lastName) {
          zendesk.setRequester({
            email: zendesk.ticket.requester.email,
            name: [salesforce.account.firstName, salesforce.account.lastName]
              .filter(Boolean)
              .join(' ')
          })
          // Clear stale search hints once we have a real customer
          searchHint.value = null
        }
        // Show "not found" when no customer data loaded
        if (
          !salesforce.account &&
          !salesforce.proofs.length &&
          !searchHint.value
        ) {
          searchHint.value = `No customer found for "${email}"`
        }
        if (salesforce.account?.clientId) {
          try {
            await paperCulture.fetchOrders({
              clientId: salesforce.account.clientId,
              authToken: salesforce.accessToken
            })
          } catch (e: unknown) {
            // Don't let a Paper Culture API failure crash the whole data flow —
            // Salesforce data still renders, orders section just shows empty
            console.error('[App] Paper Culture fetchOrders failed:', e)
          }
          // Cross-reference: if we searched by proof, find which order contains it
          if (highlightProofId.value && !highlightOrderNumber.value) {
            const matchingOrder = paperCulture.orders.find((o) =>
              o.orderItems.some(
                (item) => item.proofId === highlightProofId.value
              )
            )
            if (matchingOrder) {
              highlightOrderNumber.value = matchingOrder.orderNumber
            }
          }
        } else {
          paperCulture.clearOrders()
        }
      } else if (showDemoPreview) {
        // Unauthenticated with ?demo param: show demo data preview
        await loadDemoData()
      }
    }

    const toastVisible = ref(false)
    const toastMessage = ref('')
    function showToast(message: string) {
      toastMessage.value = message
      toastVisible.value = true
    }

    async function selectMatch(email: string) {
      const match = accountMatches.value.find((m) => m.email === email)
      if (match) {
        searchBox.value?.setEmail(email)
        zendesk.setRequester({ email, name: match.name })
        // Update highlights from the selected match
        highlightProofId.value = match.matchedProofId || null
        highlightOrderNumber.value = match.matchedOrderNumber || null
        try {
          await salesforce.fetchAll(email)
        } catch (e: unknown) {
          if (!salesforce.accessToken) return
          throw e
        }
        if (salesforce.account?.clientId) {
          try {
            await paperCulture.fetchOrders({
              clientId: salesforce.account.clientId,
              authToken: salesforce.accessToken!
            })
          } catch (e: unknown) {
            console.error('[App] Paper Culture fetchOrders failed:', e)
          }
          // Cross-reference proof → order for highlighting
          if (highlightProofId.value && !highlightOrderNumber.value) {
            const matchingOrder = paperCulture.orders.find((o) =>
              o.orderItems.some(
                (item) => item.proofId === highlightProofId.value
              )
            )
            if (matchingOrder) {
              highlightOrderNumber.value = matchingOrder.orderNumber
            }
          }
        } else {
          paperCulture.clearOrders()
        }
      }
    }

    function onLiveFilter(query: string) {
      liveFilterQuery.value = query
      if (!query || query.length < 2) {
        liveFilterResult.value = null
        return
      }
      const orderNumbers = paperCulture.orders.map((o) => o.orderNumber)

      // ── Build lookup maps ──────────────────────────────────────────
      // Unpaid proof IDs (displayed in the Proofs section)
      const unpaidProofs = salesforce.proofs.filter((p) => !p.paid)
      const unpaidProofIds = unpaidProofs
        .map((p) => p.proofId)
        .filter((id): id is string => !!id)
      const unpaidSet = new Set(unpaidProofIds)

      // Proof IDs from order items → parent order number
      const proofIdToOrderNumber = new Map<string, string>()
      // Order number → its order items' proof IDs
      const orderToProofIds = new Map<string, string[]>()
      for (const order of paperCulture.orders) {
        const itemProofIds: string[] = []
        for (const item of order.orderItems) {
          if (item.proofId) {
            proofIdToOrderNumber.set(item.proofId, order.orderNumber)
            itemProofIds.push(item.proofId)
          }
        }
        if (itemProofIds.length) {
          orderToProofIds.set(order.orderNumber, itemProofIds)
        }
      }

      // Add-on proof → parent projectId, projectId → all sibling proofIds
      const proofIdToProjectId = new Map<string, string>()
      const projectIdToProofIds = new Map<string, string[]>()
      for (const proof of unpaidProofs) {
        const pid = proof.proofId
        const projId = proof.project?.projectId
        if (pid && projId) {
          proofIdToProjectId.set(pid, projId)
          const siblings = projectIdToProofIds.get(projId) || []
          siblings.push(pid)
          projectIdToProofIds.set(projId, siblings)
        }
      }

      // ── Run the filter ─────────────────────────────────────────────
      const allProofIds = [
        ...new Set([...unpaidProofIds, ...proofIdToOrderNumber.keys()])
      ]
      const result = filterLocalData(query, orderNumbers, allProofIds)

      if (result.matches.length === 0) {
        liveFilterResult.value = null
        return
      }

      // ── Propagate matches across sections ──────────────────────────
      const matchedOrderNumbers = new Set(result.orderNumbers)
      const matchedProofIds = new Set<string>()

      // Proof matches → add parent order, expand add-on to parent+siblings
      for (const proofId of result.proofIds) {
        matchedProofIds.add(proofId)
        // If proof is in an order, show that order
        const orderNum = proofIdToOrderNumber.get(proofId)
        if (orderNum) matchedOrderNumbers.add(orderNum)
        // If proof is an add-on, include its parent and all siblings
        if (unpaidSet.has(proofId)) {
          const projId = proofIdToProjectId.get(proofId)
          if (projId) {
            const siblings = projectIdToProofIds.get(projId) || []
            for (const sib of siblings) matchedProofIds.add(sib)
          }
        }
      }

      // Order matches → show their proofs in the Proofs section too
      for (const orderNum of matchedOrderNumbers) {
        const itemProofIds = orderToProofIds.get(orderNum) || []
        for (const pid of itemProofIds) {
          // Only add unpaid proofs (the ones visible in Proofs section)
          if (unpaidSet.has(pid)) {
            matchedProofIds.add(pid)
            // Also expand to parent+siblings
            const projId = proofIdToProjectId.get(pid)
            if (projId) {
              const siblings = projectIdToProofIds.get(projId) || []
              for (const sib of siblings) matchedProofIds.add(sib)
            }
          }
        }
      }

      // Only include unpaid proofs for the Proofs section filter
      const filteredProofIds = [...matchedProofIds].filter((id) =>
        unpaidSet.has(id)
      )

      liveFilterResult.value = {
        orderNumbers: [...matchedOrderNumbers],
        proofIds: filteredProofIds,
        matches: result.matches
      }
    }

    /**
     * Handle search submission from the search box.
     *
     * If there are live filter matches on the current customer's data,
     * lock them in without making an API call. Only fall back to the
     * API search when no local match is found.
     *
     * Shift+Enter forces the API search regardless of local matches.
     */
    async function onSearch({
      value,
      format: fmt,
      force
    }: {
      value: string
      format: string
      force: boolean
    }) {
      // Check for local matches first (unless forced or it's an email)
      if (!force && fmt !== 'email' && liveFilterResult.value) {
        // Local matches found — lock them in without API call
        // Keep the current customer data, just apply the filter highlight
        searchHint.value = null
        searchBox.value?.onSearchComplete(zendesk.ticket.requester.email, value)
        return
      }

      // Validate format only when doing an API search
      if (fmt === 'unknown') {
        if (value.length < 3) {
          searchBox.value?.setError(
            'Type at least 3 characters to search all customers'
          )
        } else {
          searchBox.value?.setError(
            'Enter an email, phone, order #, or proof ID prefix'
          )
        }
        liveFilterResult.value = null
        return
      }

      // No local matches or forced — do the full API search.
      // Show a skeleton during the search so the iframe has real content to
      // measure: fetchAuthenticated clears customer data up front to avoid
      // showing stale results, which would otherwise collapse the iframe.
      liveFilterResult.value = null
      isSearching.value = true
      searchBox.value?.setLoading(true)
      try {
        zendesk.setRequester({ email: value })
        await fetchAuthenticated()
        const resolvedEmail = zendesk.ticket.requester.email
        await zendesk.lookupRequesterByEmail(resolvedEmail)
        await zendesk.fetchTickets()
        if (isOutsideZendesk) {
          localStorage.setItem(
            LAST_DEV_EMAIL_KEY,
            fmt === 'phone'
              ? (searchBox.value?.displayValue ?? resolvedEmail)
              : resolvedEmail
          )
        }
        searchBox.value?.onSearchComplete(resolvedEmail)
      } catch (e) {
        console.error('[App] search failed:', e)
        searchBox.value?.setError('Lookup failed')
      } finally {
        isSearching.value = false
        searchBox.value?.setLoading(false)
      }
    }

    function forceSearchFromHint() {
      // Trigger force search using current search box value
      const el = searchBox.value
      if (!el) return
      const value: string = el.displayValue?.trim()
      if (!value) return
      const fmt = detectFormat(value)
      onSearch({ value, format: fmt, force: true })
    }

    provide('closeAppMenu', () => (showMenu.value = false))
    provide('showToast', showToast)

    async function onConnectToSalesforce(accessToken: string) {
      if (accessToken) {
        // After authenticating, restore the user's previously stored email
        // so they resume where they left off instead of staying on the demo.
        if (isOutsideZendesk) {
          const storedEmail = getStoredDevEmail()
          if (storedEmail) {
            zendesk.setRequester({ email: storedEmail })
          }
        }
        await fetchAuthenticated()
      }
    }

    async function initialize() {
      // First, load the Salesforce Access Token to set up initial state
      await salesforce.loadAccessToken()

      // Get all initial data that doesn't require additional authentication
      await zendesk.fetchAll()

      // Defer loading analytics until we have the current user's ID, and to prioritize loading critical data.
      // However, we initialize ASAP once we have the user ID in case any other code wants to log analytics.
      // Otherwise, `window.gtag` won't be defined.
      // Don't wait for this before proceeding, because analytics can be initialized in the background.
      initializeAnalytics({
        user_id: zendesk.currentUser.id.toString()
      })

      // If already authenticated on load (token restored from localStorage),
      // restore the stored email before fetching data.
      if (isOutsideZendesk && salesforce.accessToken) {
        const storedEmail = getStoredDevEmail()
        if (storedEmail) {
          zendesk.setRequester({ email: storedEmail })
        }
      }

      // Fetch user type in background once authenticated
      if (salesforce.accessToken) {
        fetchUserInfo()
      }

      // Once the Salesforce Access Token is available, we can fetch data from Salesforce and Mondrian
      store.watch(
        () => salesforce.accessToken as string,
        (accessToken: string) => {
          loadingPromise.value = onConnectToSalesforce(accessToken)
          fetchUserInfo()
        }
      )
      await fetchAuthenticated()
    }

    if (isOutsideZendesk) {
      document.documentElement.classList.add('outside-zendesk')
    }

    onMounted(() => {
      // Set initial iframe height ASAP.
      resize()
      if (content.value) {
        // Keep the iframe height in sync with the content box. ResizeObserver catches almost
        // everything (layout, expand/collapse, search results, async data), but deferred
        // browser work (image decode, webfont swap, window resize) can shift layout after
        // the observer has already fired, so we also listen for those explicitly.
        new ResizeObserver(() => resize()).observe(content.value)
      }
      // Images loading late can grow content after the initial observer fire.
      window.addEventListener('load', () => resize())
      // Webfonts swapping in changes text metrics and line-wrapping.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => resize())
      }
      // Zendesk can resize the sidebar itself; re-measure so wrapping changes are honored.
      window.addEventListener('resize', () => resize())
    })

    const loadingPromise = ref(initialize())
    const promised = usePromise(loadingPromise)

    return {
      content,
      vueError,
      showVueError,
      // Context menu
      showMenu,
      menuX,
      menuY,
      openMenu,
      showRawData,
      clearTokens,
      copyTokens,
      copyLabel,
      showSalesforceUser,
      salesforceUserLabel,
      showUserSnackbar,
      toastVisible,
      toastMessage,
      userTypeLabel: computed(() => {
        if (!userType.value) return 'Salesforce User'
        // e.g. "STANDARD" → "Standard User", "CspLitePortal" → "Community User"
        const raw = userType.value
        if (
          raw.toLowerCase().includes('portal') ||
          raw.toLowerCase().includes('community')
        )
          return 'Community User'
        return (
          raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase() + ' User'
        )
      }),
      localDev,
      claudePreview,
      branch: env.VUE_APP_BRANCH || 'local',
      commitRef: env.VUE_APP_COMMIT_REF
        ? env.VUE_APP_COMMIT_REF.substr(0, 7)
        : 'dev',
      buildAge: formatBuildAge(),
      ...promised,
      searchBox,
      showDemoPreview,
      hasCustomerData: computed(
        () =>
          !!salesforce.account ||
          salesforce.proofs.length > 0 ||
          paperCulture.orders.length > 0
      ),
      accountMatches,
      searchHint,
      searchQuery,
      highlightProofId,
      highlightOrderNumber,
      accountMatchItems: computed(() =>
        accountMatches.value.map((m) => ({
          ...m,
          label: `${m.name} (${m.email})`
        }))
      ),
      selectMatch,
      onLiveFilter,
      onSearch,
      forceSearchFromHint,
      liveFilterResult,
      liveFilterQuery,
      isSearching,
      isDemoActive: computed(
        () =>
          (showDemoPreview && !salesforce.accessToken) ||
          isDemoEmail(zendesk.ticket?.requester?.email)
      ),
      accessToken: computed(() => salesforce.accessToken),
      account: computed(() => salesforce.account),
      // TODO: Refactor stores to be normalized so each object can be resolved by ID
      orders: computed(() => paperCulture.orders),
      // TODO: Use separate promise for loading proofs so they can display first
      proofs: computed(() => salesforce.proofs),
      unpaidProofs: computed(() =>
        salesforce.proofs.filter((proof) => !proof.paid)
      ),
      salesforceOrders: computed(() => salesforce.orders),
      fetchErrors: computed(() => salesforce.fetchErrors),
      ticket: computed(() => zendesk.ticket),
      tickets: computed(() => zendesk.userTickets),
      originalRequesterEmail: computed(() => zendesk.originalRequesterEmail),
      outsideZendesk: isOutsideZendesk,
      requesterEmail: computed(() => zendesk.ticket?.requester?.email ?? '')
    }
  }
})
</script>

<style>
html:not(.outside-zendesk) {
  /**
   * Never show the scrollbar because we'll always resize the container to fit the content.
   * Although `auto` doesn't show the scrollbar when the content fits, container resizing
   * is done asynchronously and slightly lags - behind especially during animations.
   * Using `hidden` prevents flickering by ensuring that the scrollbar doesn't show momentarily while resizing.
   */
  overflow-y: hidden;
}
.search-highlight {
  /* Use inset box-shadow instead of border to avoid shifting content */
  box-shadow: inset 3px 0 0 #1f73b7 !important;
  background-color: rgba(31, 115, 183, 0.06) !important;
}
.filter-search-link {
  cursor: pointer;
  text-decoration: underline dotted;
  text-underline-offset: 2px;
  transition: color 0.15s ease;
}
.filter-search-link:hover {
  color: rgba(0, 0, 0, 0.6);
}

/* Match dropdown: align icon with SearchBox's left icon */
.match-dropdown .v-input__prepend-inner,
.match-dropdown .v-input__append-inner {
  align-self: center;
  margin-top: 0 !important;
}
.match-dropdown-icon {
  font-size: 13px !important;
  width: 16px;
}
/**
 * Force the selection onto a single line. `.v-select__selections` defaults to
 * `flex-wrap: wrap`, which lets our "N matches · <hint>" text break to a second
 * line when `searchHint` is long (e.g. "Found via phone X and order Y") during
 * the loading window, then collapse back once `searchHint` is cleared on load.
 */
.match-dropdown .v-select__selections {
  flex-wrap: nowrap;
  overflow: hidden;
}
.match-dropdown .v-select__selections > span {
  display: block;
  min-width: 0;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
